import { useState, useEffect, useRef } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { TbX } from "react-icons/tb";
import IconDropdown from "./IconDropdown";
import IconCircle from "./IconCircle";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

// Simple hook to detect mobile (md breakpoint)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

const Dropdown = ({
  options,
  icon = BsThreeDotsVertical,
  iconComponent = "dropdown",
  rotateOnOpen = false,
  isOpen: controlledIsOpen,
  onOpenChange,
  onSelect, // for mobile sheet
  selectedValue, // for active state
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const isMobile = useIsMobile();

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalOpen;

  const toggleDropdown = (e) => {
    e?.preventDefault?.();
    const newState = !isOpen;
    if (!isControlled) {
      setInternalOpen(newState);
    }
    onOpenChange?.(newState);
  };

  const handleSelect = (option, e) => {
    if (option.onClick) {
      e?.preventDefault?.();
      option.onClick();
      if (!isControlled) {
        setInternalOpen(false);
      }
      onOpenChange?.(false);
    } else if (option.link && !option.external) {
      e?.preventDefault?.();
      window.location.href = option.link;
      if (!isControlled) {
        setInternalOpen(false);
      }
      onOpenChange?.(false);
    }
    // External links - default behavior
  };

  // Desktop dropdown positioning
  useEffect(() => {
    if (!isMobile && isOpen && triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();

      let newLeft = triggerRect.left + window.scrollX;
      let newTop = triggerRect.bottom + window.scrollY;

      // Prioritize opening to the left if near right edge of screen
      if (newLeft + menuRect.width + 8 > window.innerWidth) {
        newLeft = triggerRect.right + window.scrollX - menuRect.width;
      }

      // Ensure it's not off the left edge
      if (newLeft < 8) {
        newLeft = 8;
      }

      // Ensure it's not off the right edge (final check after left adjustment)
      if (newLeft + menuRect.width + 8 > window.innerWidth) {
        newLeft = window.innerWidth - menuRect.width - 8;
      }

      // Check for vertical overflow (clamp to bottom if needed)
      // REMOVED: Logic to move dropdown upwards if it overflows bottom

      // Ensure it's not off the top edge (shouldn't happen if always below, but as a safeguard)
      // REMOVED: Logic to clamp to top of viewport

      setMenuPosition({ top: newTop, left: newLeft, width: menuRect.width });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMobile, window.innerWidth, window.innerHeight]);

  // Close on outside click (both mobile and desktop)
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        if (!isControlled) setInternalOpen(false);
        onOpenChange?.(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, isControlled, onOpenChange]);

  const renderIcon = (icon) => {
    if (typeof icon === "string") {
      return <img src={icon} alt="flag" className="w-5 h-5" />;
    }
    return icon;
  };

  const IconComponent = iconComponent === "circle" ? IconCircle : IconDropdown;

  // --- Desktop Dropdown ---
  const desktopDropdown =
    isOpen &&
    !isMobile &&
    createPortal(
      <ul
        ref={menuRef}
        className="z-[9999] list-none m-0 p-0 absolute drop-shadow-md card-base card-base-border card-base-background w-max min-w-[120px] overflow-hidden"
        style={{
          top: menuPosition.top,
          left: menuPosition.left,
          position: "absolute",
        }}
      >
        {options.map((option, index) => (
          <li
            key={index}
            className={`cursor-pointer py-2 px-3 hover:bg-blue-100 transition ease-in-out duration-300 ${
              selectedValue === option.text ? 'bg-blue-200' : ''
            }`}
          >
            <a
              className="flex flex-row gap-2"
              href={option.link || "#"}
              onClick={(e) => handleSelect(option, e)}
              target={option.external ? "_blank" : "_self"}
              rel={option.external ? "noopener noreferrer" : undefined}
            >
              {option.icon && (
                <span className="justify-center flex items-center w-4">
                  {renderIcon(option.icon)}
                </span>
              )}
              <span className="text-sm">{option.text}</span>
            </a>
          </li>
        ))}
      </ul>,
      document.body
    );

  // --- Mobile Sheet Dropdown ---
  const mobileDropdown =
    isOpen &&
    isMobile &&
    createPortal(
      <div className="fixed inset-0 z-[9999] flex items-end md:hidden">
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/30"
          onClick={() => setInternalOpen(false)}
        />
        {/* Sheet */}
        <div
          ref={menuRef}
          className="relative w-full background-page-color rounded-t-xl shadow-2xl p-4 pb-10 animate-slide-up max-h-[80vh] overflow-y-auto"
        >
          {/* X button */}
          <div className="flex justify-end z-20 !mb-2">
            <IconCircle
              icon={TbX}
              onClick={() => {
                setInternalOpen(false);
                onOpenChange?.(false);
              }}
              ariaLabel="Close"
            />
          </div>

          {/* Options - Use grid for long lists */}
          <div className={
            options.length > 8 
              ? 'grid grid-cols-2 gap-2' 
              : 'flex flex-col gap-2'
          }>
            {options.map((option, idx) =>
              option.link ? (
                <a
                  key={idx}
                  href={option.link}
                  target={option.external ? "_blank" : "_self"}
                  rel={option.external ? "noopener noreferrer" : undefined}
                  className={`flex items-center w-full py-3 px-4 text-base text-left bg-white transition card-base-border rounded-md ${
                    options.length > 8 
                      ? 'justify-center gap-2' 
                      : 'gap-4'
                  }`}
                  onClick={() => {
                    setInternalOpen(false);
                    onOpenChange?.(false);
                    onSelect && onSelect(option);
                  }}
                >
                  {option.icon && (
                    <span className={`${options.length > 8 ? 'text-lg' : 'text-2xl'}`}>
                      {renderIcon(option.icon)}
                    </span>
                  )}
                  <span className={options.length > 8 ? 'text-sm' : 'text-lg'}>
                    {option.text}
                  </span>
                </a>
              ) : (
                <button
                  key={idx}
                  className={`flex items-center w-full py-3 px-4 text-base text-left bg-white hover:bg-gray-100 focus:bg-gray-100 transition card-base-border rounded-md ${
                    options.length > 8 
                      ? 'justify-center gap-2' 
                      : 'gap-4'
                  }`}
                  onClick={() => {
                    setInternalOpen(false);
                    onOpenChange?.(false);
                    option.onClick && option.onClick();
                    onSelect && onSelect(option);
                  }}
                >
                  {option.icon && (
                    <span className={`${options.length > 8 ? 'text-lg' : 'text-2xl'}`}>
                      {renderIcon(option.icon)}
                    </span>
                  )}
                  <span className={options.length > 8 ? 'text-sm' : 'text-lg'}>
                    {option.text}
                  </span>
                </button>
              )
            )}
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div className="relative inline-block">
      <div className="z-10 relative" ref={triggerRef}>
        <IconComponent
          icon={icon}
          onClick={toggleDropdown}
          isOpen={isOpen}
          rotateOnOpen={rotateOnOpen}
        />
      </div>
      {desktopDropdown}
      {mobileDropdown}
    </div>
  );
};

Dropdown.propTypes = {
  options: PropTypes.array.isRequired,
  icon: PropTypes.elementType,
  iconComponent: PropTypes.string,
  rotateOnOpen: PropTypes.bool,
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onSelect: PropTypes.func,
  selectedValue: PropTypes.string,
};

export default Dropdown;
